<script setup lang="ts">
/**
 * VaultInspector — H4 da Proposta Lume, Fase 4.
 *
 * Mostra o que está guardado no vault local. Soberania visível
 * (P-UX-05): em qualquer tela, o usuário pode ver o que está
 * armazenado, com explicação de cada item.
 *
 * Constitucional:
 * - Art II §2.5 (Soberania do Aprendiz): o modelo é do aprendiz.
 * - Art VII (Dissolução): o vault volta para o dono.
 * - CL-07 (Modelo = ativo, local-first): vault local, plataforma
 *   nunca acessa a chave.
 * - P-UX-05 (Sovereignty tangible): ≤ 2 toques para chegar aqui.
 * - LGPD: zero PII em logs; este componente NÃO exibe conteúdo
 *   das mensagens, apenas contagens e categorias.
 *
 * NOTA: VaultAPI atual expõe apenas readData/writeData. Categorias
 * específicas (theta, goals, memory) virão em versões futuras quando
 * o kernel ganhar interface de leitura por categoria. Por ora, este
 * componente mostra a lista canônica de categorias + ações de
 * soberania (export, delete).
 */
import { ref } from "vue";

interface VaultItemSummary {
  category: string;
  label: string;
  description: string;
  /** Quando true, item é editável; false = somente leitura. */
  editable: boolean;
}

const items: VaultItemSummary[] = [
  {
    category: "theta",
    label: "Modelo latente θ_lat",
    description: "Suas 6 dimensões cognitivas inferidas (LATENT_DIM=6, imutável).",
    editable: false,
  },
  {
    category: "goals",
    label: "Metas",
    description: "Objetivos que você declarou ou que o Companion infere.",
    editable: true,
  },
  {
    category: "memory",
    label: "Memórias",
    description: "Fatos biográficos que você guardou para personalização.",
    editable: true,
  },
  {
    category: "focus",
    label: "Foco atual",
    description: "Sua stack de atenção — o que importa para você agora.",
    editable: true,
  },
  {
    category: "sessions",
    label: "Sessões",
    description: "Histórico de conversas (criptografado, local).",
    editable: true,
  },
  {
    category: "preferences",
    label: "Preferências de UI",
    description: "Tema, acessibilidade, voz (não é parte do modelo do aprendiz).",
    editable: true,
  },
  {
    category: "learning_state",
    label: "Estado de aprendizado",
    description: "Progresso nos LX (atividades pedagógicas).",
    editable: true,
  },
  {
    category: "canon",
    label: "Canon pessoal",
    description: "Suas reflexões, metáforas, mudanças de ideia (você é dono).",
    editable: true,
  },
];

const showFullList = ref(false);

interface Emits {
  (e: "close"): void;
  (e: "export-all"): void;
  (e: "delete-all"): void;
}

const emit = defineEmits<Emits>();

function onExport() {
  emit("export-all");
}

function onDelete() {
  if (
    confirm(
      "Tem certeza? Esta ação apaga TUDO do seu Companion local. " +
        "Esta decisão não pode ser desfeita.",
    )
  ) {
    emit("delete-all");
  }
}
</script>

<template>
  <section
    class="hlos-vault-inspector"
    role="region"
    aria-labelledby="hlos-vault-title"
    data-testid="vault-inspector"
  >
    <header class="hlos-vault-inspector__header">
      <h2 id="hlos-vault-title">Seu vault</h2>
      <p class="hlos-vault-inspector__intro">
        Tudo o que o Companion guarda sobre você. Criptografado
        localmente. A plataforma nunca tem acesso à chave.
      </p>
    </header>

    <div class="hlos-vault-inspector__status">
      <span class="hlos-vault-inspector__lock" data-testid="vault-status">
        <span aria-hidden="true">🔒</span> Criptografia ativa
      </span>
    </div>

    <h3 class="hlos-vault-inspector__sub">
      Categorias
      <button
        type="button"
        class="hlos-vault-inspector__toggle"
        :aria-expanded="showFullList"
        data-testid="vault-toggle-categories"
        @click="showFullList = !showFullList"
      >
        {{ showFullList ? "Ocultar" : "Ver todas" }}
      </button>
    </h3>

    <ul
      class="hlos-vault-inspector__list"
      :data-expanded="showFullList"
      aria-label="Categorias de dados no vault"
    >
      <li
        v-for="(item, i) in showFullList ? items : items.slice(0, 3)"
        :key="item.category"
        class="hlos-vault-inspector__item"
        :data-testid="`vault-item-${item.category}`"
        v-show="showFullList || i < 3"
      >
        <div class="hlos-vault-inspector__item-info">
          <strong>{{ item.label }}</strong>
          <span class="hlos-vault-inspector__item-desc">{{ item.description }}</span>
        </div>
        <span class="hlos-vault-inspector__item-badge">
          {{ item.editable ? "editável" : "imutável" }}
        </span>
      </li>
    </ul>

    <footer class="hlos-vault-inspector__actions">
      <button
        type="button"
        class="hlos-vault-inspector__btn hlos-vault-inspector__btn--primary"
        data-testid="vault-export"
        @click="onExport"
      >
        Exportar tudo
      </button>
      <button
        type="button"
        class="hlos-vault-inspector__btn hlos-vault-inspector__btn--danger"
        data-testid="vault-delete"
        @click="onDelete"
      >
        Apagar tudo
      </button>
      <button
        type="button"
        class="hlos-vault-inspector__btn hlos-vault-inspector__btn--tertiary"
        data-testid="vault-close"
        @click="emit('close')"
      >
        Fechar
      </button>
    </footer>
  </section>
</template>

<style scoped>
.hlos-vault-inspector {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
}
.hlos-vault-inspector__header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
}
.hlos-vault-inspector__intro {
  margin: 0 0 1rem;
  color: var(--hlos-color-neutral-600);
  font-size: 0.9em;
}
.hlos-vault-inspector__status {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: color-mix(in srgb, var(--hlos-color-success) 10%, transparent);
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9em;
}
.hlos-vault-inspector__sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  margin: 0 0 0.5rem;
}
.hlos-vault-inspector__toggle {
  font: inherit;
  font-size: 0.85em;
  border: none;
  background: transparent;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}
.hlos-vault-inspector__list {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.hlos-vault-inspector__item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 6px;
  gap: 0.5rem;
}
.hlos-vault-inspector__item-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
}
.hlos-vault-inspector__item-desc {
  font-size: 0.8em;
  color: var(--hlos-color-neutral-600);
}
.hlos-vault-inspector__item-badge {
  font-size: 0.75em;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: var(--hlos-color-neutral-100);
  color: var(--hlos-color-neutral-700);
  white-space: nowrap;
}
.hlos-vault-inspector__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: space-between;
}
.hlos-vault-inspector__btn {
  font: inherit;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}
.hlos-vault-inspector__btn--primary {
  background: var(--hlos-color-primary);
  color: var(--hlos-color-bg);
  border: 1px solid var(--hlos-color-primary);
}
.hlos-vault-inspector__btn--danger {
  background: transparent;
  border: 1px solid var(--hlos-color-error);
  color: var(--hlos-color-error);
}
.hlos-vault-inspector__btn--tertiary {
  background: transparent;
  border: 1px solid currentColor;
}
</style>
